import React from "react";
import { useNavigate } from "react-router-dom";

import carbonFootprint from "../../../assets/carbon-footprint.webp";
import { exploreSurveyIntentPath } from "../../../configs/ExploreSurveyConfig";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import {
  IDashboardCarbonPayload,
  IDashboardTileResult,
} from "../../../services/zeos-api/contracts/Dashboard";
import { DashboardMetricTile } from "../shared/DashboardMetricTile";

// Avoided carbon is production x emission factor, so either input being estimated marks the figure.
// Produced is bills x factor, so only the factor can mark it.
const carbonAlert = (cefIsEstimated?: boolean, productionIsEstimated?: boolean) => {
  const reasons = [
    cefIsEstimated ? "uses a default emission factor because the zip/EPA rate is unavailable" : null,
    productionIsEstimated ? "includes estimated solar production for months the vendor reported no data" : null,
  ].filter(Boolean);
  return reasons.length ? `Estimated: ${reasons.join("; ")}.` : undefined;
};

interface IProps {
  setUnlocked: (unlocked: boolean) => void;
  unlocked: boolean;
  tileResult?: IDashboardTileResult<IDashboardCarbonPayload>;
  loading?: boolean;
}

export const CarbonEmissions = (props: IProps) => {
  const navigate = useNavigate();
  const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
  const data = props.tileResult?.data;

  return (
    <DashboardMetricTile
      tileKey="carbon"
      title="Carbon Emissions"
      image={carbonFootprint}
      navPath={pageConfigBySlug("carbon")?.slug || "carbon"}
      unlocked={props.unlocked}
      setUnlocked={props.setUnlocked}
      tileResult={props.tileResult}
      loading={props.loading}
      lockedCopy={{
        bodyHeading: "CUT CARBON, BOOST VALUE.",
        bodyContent:
          "Track your emissions and discover how projects can slash your carbon footprint—while unlocking compliance benefits and cost savings.",
        onExploreClick: () =>
          navigate(exploreSurveyIntentPath(exploreSlug, "carbon-emissions")),
      }}
      // Unit lives in the label (tCO₂e) so it can't be missed as a small suffix; tCO₂e per Task 4025.
      // 4100: Produced from the bills whenever there are any; Avoided only with on-site solar (3535).
      metrics={[
        {
          label: "tCO₂e Produced",
          value: data?.emissionsProduced,
          tone: "neutral",
          alert: data?.emissionsProduced != null ? carbonAlert(data?.cefIsEstimated) : undefined,
        },
        ...(data?.emissionsAvoided != null
          ? [
              {
                label: "tCO₂e Avoided",
                value: data.emissionsAvoided,
                tone: "positive" as const,
                alert: carbonAlert(data.cefIsEstimated, data.productionIsEstimated),
              },
            ]
          : []),
      ]}
      // Produced is electric only until gas is ingested (DV-02 F-BP5); said, not left silent.
      footer={
        data?.fuelScope ? (
          <p className="m-0 mt-1 px-4 text-xs text-black/70 text-center" data-testid="carbon-fuel-scope">
            {data.fuelScope}
          </p>
        ) : null
      }
    />
  );
};
